import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { v4 as uuid4 } from 'uuid';
import { Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { TranslatePipe } from '../../pipes/translate.pipe';
import {
  AnnotationService,
  onAddingAnnotation,
  onCancelAddingAnnotation,
  onCommentsVisibilityChange,
  onEditingAnnotation,
  onImageInitialResize,
  onImageZoomChanged,
  onMediaDownloadClick,
  onUserColourCreated,
  onConversationPluginReady,
  onPluginReady,
  onOpenAnnotation,
} from '../../services/annotation.service';
import { AuthService } from '../../services/auth.service';
import { commentFileAdd } from '../../services/comments.service';
import { EmitterService } from '../../services/emitter.service';
import { MtmStompService, wsListenerANNOTATION_CREATED } from '../../services/mtm-stomp.service';
import { OverlayService } from '../../services/overlayService';
import { ProjectService } from '../../services/project.service';
import { SubsectionService } from '../../services/subsection.service';
import { TranslateService } from '../../services/translate.service';
import {
  ActiveSubsectionState,
  AiOptions,
  aiParamsFromAnnotationEvent,
  AnnotationPrivateCondition,
  AnnotationSendMediaMessage,
  OptionInfo,
  QueryState,
} from '../../interfaces';
import { PermissionService } from '../../services/permissions.service';
import { checkInvalidUser } from '../../utils/permission-checks';
import { MTMFileDownloadControl } from '../mtm-file-download/mtm-file-download-control/mtm-file-download-control.component';
import { ReviewUiLinkSettingsComponent } from '../review-link/review-link-settings/review-link-settings.component';
import { ImageAnnotationPlugin } from './plugin/image-annotation.plugin';
import { ImageAnnotationHandler } from '../image-annotation-handler/image-annotation-handler';
// import { CommentAnnotationPlugin } from "../comment-annotation/plugin/comment-annotation.plugin";
// import { CommentAnnotationPlugin } from "annotation";
import { CommentAnnotationPlugin, Environment, PluginConfig } from '@mtm/conversation-client-sdk-annotation';
import { HttpClient } from '@angular/common/http';
import { PlatformRbacSignatureService, SignatureRequestResult } from '../../services/platform-rbac-signature.service';
import { environment } from '../../../../environments/environment';
import { commentHasAnnotation } from '../../base-functions/annotation';
import { HelperService } from '../../services/helper.service';
import { UserService } from '../../services/user.service';
import { GeneralUtilsService } from '../../services/general-utils.service';
import { FileTypeService } from '../../services/file-type.service';
import { TopMediaInfoService } from '../top-media-info/top-media-info.service';
import { MediaPlayerStoreService } from '../../services/media-player-store.service';

@Component({
  selector: 'mtm-image-annotation',
  styleUrl: './image-annotation.component.scss',
  templateUrl: './image-annotation.component.html',
  standalone: false,
})
export class ImageAnnotation implements OnInit, OnDestroy, AfterViewInit {
  private readonly subsectionService = inject(SubsectionService);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly modalService = inject(NgbModal);
  private readonly overlayService = inject(OverlayService);
  private readonly projectService = inject(ProjectService);
  private readonly permissionService = inject(PermissionService);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly httpClient = inject(HttpClient);
  private readonly rbacSignature = inject(PlatformRbacSignatureService);
  userService = inject(UserService);
  private readonly generalUtilsService = inject(GeneralUtilsService);
  private readonly fileTypeService = inject(FileTypeService);
  private readonly mpStoreService = inject(MediaPlayerStoreService);
  private readonly ngZone = inject(NgZone);

  /** When the control bar is narrower than this (px), overflow actions go into the "more" menu. */
  readonly controlBarMoreDropdownBreakpointPx = 600;

  controlBarIsNarrow = false;

  private controlBarResizeObserver: ResizeObserver | null = null;

  @ViewChild('controlBar') set controlBar(ref: ElementRef<HTMLElement> | undefined) {
    if (this.controlBarResizeObserver) {
      this.controlBarResizeObserver.disconnect();
      this.controlBarResizeObserver = null;
    }
    const el = ref?.nativeElement;
    if (!el) {
      return;
    }
    const applyWidth = (): void => {
      const w = el.getBoundingClientRect().width;
      const narrow = w > 0 && w < this.controlBarMoreDropdownBreakpointPx;
      this.ngZone.run(() => {
        if (this.controlBarIsNarrow !== narrow) {
          this.controlBarIsNarrow = narrow;
        }
      });
    };
    this.controlBarResizeObserver = new ResizeObserver(() => {
      applyWidth();
    });
    this.controlBarResizeObserver.observe(el);
    applyWidth();
  }

  @ViewChild('imageAnnotationMarkerContainer') imageAnnotationMarkerContainer: ElementRef;
  @ViewChild('downloadButton') downloadButton: MTMFileDownloadControl;
  @Input() projectId: any;
  @Input() sectionId: any;
  @Input() participants: any[] = [];
  @Input() subsectionId: any;
  @Input() typeId: any;
  @Input() item: any;
  @Input() annotations: any[];
  @Input() authUser: any;
  @Input() hideAnnotationToolbar: boolean = true;
  @Input() hideTopMediaInfo: boolean = false;
  @Input() isSubsectionBarVisible: boolean = false;
  @Input() hasEditPermission: boolean = false;
  @Input() topMediaTemplate: TemplateRef<any>;
  @Input() assetListTemplate: TemplateRef<any>;
  @Input() assetInfoTemplate: TemplateRef<any>;
  @Input() reviewLink: any;
  @Input() instanceId: string;
  @Input() shareLinkPreview: boolean = false;
  @Input() file: any;
  @ViewChild('imageAsset') imageAsset: ElementRef;
  @ViewChild('zoomDrop', { static: false }) zoomDropRef: NgbDropdown;

  readonly MAX_MARKERS_TO_SHOW = 3;
  zoomInputValue: string | number = '';

  // editingMode: boolean = false;
  isColorOpen: boolean = false;
  activeShape: string;
  config: PluginConfig;
  annotationComment: string = '';
  pluginReady: boolean = false;
  isLoading: boolean = true;
  annotationPlugin: CommentAnnotationPlugin;
  imageInitReady: boolean = false;
  imageReadyWait: boolean = false;
  // activeAnnotation: any; // handled inside plugin
  hoveredAnnotationMessage: any = null;
  userInfo: any;
  userColors: any = {};
  isAnnotationDisplayEnabled: boolean = true; // handled inside plugin
  lang: any;
  drawingSelected: boolean = false; // handled inside plugin
  annotationToolbarActive: boolean = true;
  wsId: string;
  newFiles: any = [];
  privateCondition: AnnotationPrivateCondition = null;
  isBusy: boolean = false;

  files: any = [];
  videoRecordFiles: any = [];
  audioRecordFiles: any = [];
  ngUnsubscribe = new Subject();
  subSectionState: ActiveSubsectionState;
  isAssetListVisible: boolean;
  optionsState: QueryState<OptionInfo>;
  imageHandler: ImageAnnotationHandler;
  // annotatingMode: boolean = false;
  editingAnnotation: any = null;
  signature: any;

  get activeAnnotation() {
    const pluginActiveAnnotation = this.annotationPlugin?.annotationState?.activeAnnotation;
    if (pluginActiveAnnotation?.type === 'click') {
      return pluginActiveAnnotation;
    }
    if (this.hoveredAnnotationMessage) {
      return {
        data: this.hoveredAnnotationMessage,
        type: 'hover',
        username: this.getAnnotationUsername(this.hoveredAnnotationMessage),
      };
    }
    return null;
  }

  private getAnnotationUsername(annotation: any): string | undefined {
    return (
      annotation?.comments?.[0]?.meta?.user_id ||
      annotation?.account?.email ||
      annotation?.accountEmail ||
      annotation?.commentList?.comments?.[0]?.meta?.user_id
    );
  }

  get isAdding() {
    return this.annotationPlugin?.controls?.uiState?.adding && this.annotationPlugin?.controls?.uiState?.drawingEnabled;
  }

  get annotatingMode() {
    return this.isAdding || false;
  }

  get editingMode() {
    return this.annotationPlugin?.controls?.uiState?.editing || false;
  }

  get isFullscreen() {
    return this.isCompareMode ? this.compareViewerFullscreen : this.topMediaInfoService.isFullscreen;
  }

  //   get annotatingMode() {
  //     return this.annotationPlugin?.controls?.uiState?.adding || false;
  //   }

  //   get editingAnnotation() {
  //     return this.annotationPlugin?.controls?.uiState?.editing || false;
  //   }

  // Resizing variables
  @ViewChild('resizeDiv') resizeDiv!: ElementRef<HTMLDivElement>;
  private resizeObserver!: ResizeObserver;
  resizeDebounceTime = 500; // in ms
  private readonly onResize = this.generalUtilsService.debounce((width: number, height: number) => {
    const resizeData = { width: width, height: height, resetViewpoint: window['res'] };
    this.imageHandler.resize(width, height, false);
    // this.resetImageOrientation();
  }, this.resizeDebounceTime);
  generalCommentingMode: boolean;
  isShareLinkPreview: boolean = false;
  isReleasePreview: boolean = false;
  isCompareMode: boolean = false;
  private compareViewerFullscreen = false;
  constructor(
    private readonly elementRef: ElementRef,
    private readonly authService: AuthService,
    private readonly stompService: MtmStompService,
    private readonly translateService: TranslateService,
    private readonly annotationService: AnnotationService
  ) {}

  generateSignature() {
    const isPage = !!this.subSectionState?.activeFile?.hasPage;
    const entityType = isPage ? 'PAGE' : 'FILE';
    const itemName = isPage ? this.subSectionState?.activePage?.name : this.item.displayName;
    return this.rbacSignature
      .requestSignature(entityType, this.item.id, itemName)
      .pipe(catchError(() => of<SignatureRequestResult | null>(null)));
  }

  prepareParticipantList() {
    const inputParticipants = this.participants || [];
    const normalizedAuthEmail = (this.authUser?.email || '').toLowerCase();

    const hasAuthUser = inputParticipants.some((p) => {
      const email = (p?.email || p?.accountEmail || '').toLowerCase();
      return email && normalizedAuthEmail && email === normalizedAuthEmail;
    });

    const mapped = inputParticipants.map((participant) => ({
      accountEmail: participant?.email || participant?.accountEmail,
      owner: (participant?.email || participant?.accountEmail) === this.authUser?.email,
    }));

    if (!hasAuthUser && normalizedAuthEmail) {
      mapped.push({
        accountEmail: this.authUser.email,
        owner: true,
      });
    }

    return mapped;
  }

  ngAfterViewInit(): void {
    // this.onImageInitReady();

    this.wsId = this.stompService.subscribeToService();
    onAddingAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onAddingAnnotationHandler(args),
    });
    onCancelAddingAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onCancelAddingAnnotationHandler(args),
    });
    onImageZoomChanged.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.setImageZoom(args),
    });
    onCommentsVisibilityChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.updateMarker(args),
    });
    onUserColourCreated.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.updateBorders(args),
    });
    onMediaDownloadClick.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => {
        if (args.instanceId != this.instanceId || args.itemId != this.item?.id) {
          return;
        }
        this.downloadAsset();
      },
    });
    onEditingAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.editAnnotation(args),
    });

    // this.stompService.subscribeToListener(
    //   'ANNOTATION_CREATED',
    //   this.wsId,
    //   [this.projectId, this.sectionId, this.subsectionId],
    //   (args: any) => this.wsOnAnnotationCreate(args),
    //   wsListenerANNOTATION_CREATED
    // );

    // ResizeObserver Initialization
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Check if the current entry's target is the resizeDiv element
        if (
          entry.target === this.resizeDiv.nativeElement &&
          this.annotationPlugin &&
          this.pluginReady &&
          this.imageHandler
        ) {
          const { width } = entry.contentRect;
          const { height } = entry.contentRect;
          this.onResize(width, height);
        }
      }
    });

    onImageInitialResize.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => {
        const { width } = args;
        const { height } = args;
        this.onResize(width, height);
      },
    });
    // Observe the canvas parent element
    this.resizeObserver.observe(this.resizeDiv.nativeElement);
  }

  ngOnInit(): void {
    window['imageAnnotation'] = this;
    this.authUser = this.authService.getAuthUser();
    const participantList = this.participants ?? [];
    this.userInfo = participantList.find((participant) => participant?.username === this.authUser?.username) as any;
    this.lang = this.translateService.getLang();
    this.annotationService.privateConditionChange$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (value: AnnotationPrivateCondition) => {
        this.privateCondition = value;
      },
    });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged(
          (prev, curr) =>
            prev.activeOption?.optionId === curr.activeOption?.optionId &&
            prev.activeVersion?.versionNumber === curr.activeVersion?.versionNumber
        )
      )
      .subscribe({
        next: (state) => {
          this.subSectionState = state;
        },
      });

    this.subsectionService.options$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (options) => {
        this.optionsState = options;
      },
    });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.activeFile === curr.activeFile)
      )
      .subscribe({
        next: (state) => {
          this.subSectionState = state;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isShareLinkPreview === curr.isShareLinkPreview)
      )
      .subscribe({
        next: (state) => {
          this.isShareLinkPreview = state.isShareLinkPreview;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isReleasePreview === curr.isReleasePreview)
      )
      .subscribe({
        next: (state) => {
          this.isReleasePreview = state.isReleasePreview;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.compareMode === curr.compareMode)
      )
      .subscribe({
        next: (state) => {
          const nextCompare = state?.compareMode ?? false;
          if (!nextCompare) {
            this.compareViewerFullscreen = false;
          }
          this.isCompareMode = nextCompare;
        },
      });

    EmitterService.get(AnnotationSendMediaMessage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          console.log('image-annotation media message data', data);
          if (this.instanceId != data.instanceId) {
            return;
          }
          if (data.message) {
            console.log('data', data);
            this.annotationComment = data.message;
            this.newFiles = data.newFiles || [];
            if (data.type === 'add') {
              this.saveAnnotation(aiParamsFromAnnotationEvent(data));
            } else if (data.type === 'edit') {
              if (this.editingMode) {
                this.saveEditedAnnotation(aiParamsFromAnnotationEvent(data));
              } else {
                this.annotationPlugin.fire('updateComment', {
                  annotationId: data.comment.annotation.id,
                  commentId: data.comment.comment.id,
                  body: data.message,
                  files: data.newFiles,
                  agentVersionId: data.agentVersionId,
                  mediaQuality: data.mediaQuality,
                });
              }
            }

            // this.editingAnnotation = null;
            // this.editingMode = false;
            this.isBusy = false;
          }
        },
      });

    EmitterService.get('annotationHoveredIn')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (event: any) => {
          if (event?.instanceId !== this.instanceId || event?.itemId !== this.item?.id) {
            return;
          }
          // this.setHoveredAnnotationMessage(event.annotation ?? null);
        },
      });

    EmitterService.get('annotationHoveredOut')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (event: any) => {
          if (event?.instanceId !== this.instanceId || event?.itemId !== this.item?.id) {
            return;
          }
          if (this.hoveredAnnotationMessage?.id && event?.annotation?.id !== this.hoveredAnnotationMessage.id) {
            return;
          }
          this.setHoveredAnnotationMessage(null);
        },
      });

    this.generateSignature().subscribe((result) => {
      this.signature = result?.signature ?? null;
      this.onImageInitReady();
    });
  }

  hasAnnotation(annotation): boolean {
    return commentHasAnnotation(annotation);
  }

  reviewLinkSettings: any;
  openReviewLinkSettings() {
    this.reviewLinkSettings = this.modalService.open(ReviewUiLinkSettingsComponent, {
      size: 'lg',
      backdrop: false,
      modalDialogClass: 'responsive-modal',
    });
    const subSection = this.projectService.project.subsectionsId[this.subsectionId];
    this.reviewLinkSettings.componentInstance.setParams({
      section: subSection.projectSectionId,
      subSection: this.subsectionId,
      parentItem: this.subSectionState.activeOption,
      assets: this.optionsState.data,
      init: true,
    });
  }

  toggleAssetList() {
    this.isAssetListVisible = !this.isAssetListVisible;
  }

  wsOnAnnotationCreate(args) {
    commentFileAdd.emit({
      projectId: this.projectId,
      sectionId: this.sectionId,
      subSectionId: this.subsectionId,
      comment: args.mediaAnnotation.comments[0],
      annotationId: args.mediaAnnotation.id,
      files: this.newFiles,
    });
    this.cleanUpFiles();
  }

  checkInvalidUser(): boolean {
    return checkInvalidUser(this.permissionService, this.reviewLink);
  }

  onImageInitReady() {
    this.config = {
      userInfo: this.authUser,
      item: this.item,
      entityName: this.subSectionState?.activeFile?.hasPage ? 'PAGE' : 'FILE',
      annotationObjects: this.annotations,
      instanceId: this.instanceId,
      itemSignature: this.signature.access_token,
      httpClient: this.httpClient,
      participants: this.prepareParticipantList(),
      environment: environment.environmentType as Environment,
      ...(this.shareLinkPreview ? { isPrivate: false } : {}),
    };
    // this.annotations = args.annotations;
    if (!this.imageInitReady) {
      this.initPlugin();
    }
    this.imageInitReady = true;
    // if (!this.imageReadyWait) this.imageReady();
  }

  filteredAnnotations() {
    if (this.annotationPlugin && this.annotationPlugin.annotationState) {
      return this.annotationPlugin.annotationState.annotationMessages as any[];
    }
    return [];
  }

  private saveImageHandlerToStore(): void {
    if (!this.instanceId || !this.imageHandler) {
      return;
    }
    const store = this.mpStoreService.getStore(this.instanceId);
    store?.updateStore({ player: this.imageHandler });
  }

  initPlugin() {
    this.annotationPlugin = new CommentAnnotationPlugin(this.config);
    // this.userInfo = this.config.userInfo;

    this.annotationPlugin.onReady(() => {
      this.imageHandler = new ImageAnnotationHandler(
        this.annotationPlugin,
        this.item,
        this.instanceId,
        this.fileTypeService
      );
      this.saveImageHandlerToStore();
      this.pluginReady = true;
      this.isLoading = false;
      onPluginReady.emit({
        annotationPlugin: this.annotationPlugin,
        instanceId: this.instanceId,
        itemId: this.item?.id,
        type: 'image',
      });
      onConversationPluginReady.emit({
        annotationPlugin: this.annotationPlugin,
        instanceId: this.instanceId,
        itemId: this.item?.id,
      });
      EmitterService.get('imageAnnotation.ready').emit();
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this.stompService.unsubscribeToService(this.wsId);

    const store = this.instanceId ? this.mpStoreService.getStore(this.instanceId) : null;
    store?.updateStore({ player: null });

    // Disconnect resizeObserver and debouncer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.controlBarResizeObserver) {
      this.controlBarResizeObserver.disconnect();
      this.controlBarResizeObserver = null;
    }
    this.onResize.cancel();
    this.imageHandler?.teardown();
  }

  onCancelAddingAnnotationHandler(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.item?.id) {
      return;
    }
    this.resetUIComponent();
  }

  onAddingAnnotationHandler(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.item?.id) {
      return;
    }
  }

  toolbarClicked(type: string) {
    if (this.imageHandler.isImageMovable && type !== 'none') {
      this.toggleImageMove();
    }
    if (type === 'color') {
      this.isColorOpen = false;
    } else {
      if (type !== 'undo' && type !== 'redo' && type !== 'clear') {
        this.activeShape = type;
      }
    }
    this.annotationPlugin.fire('changeActiveTool', { tool: type });
  }

  isInValidInput() {
    return !this.annotationComment && !this.newFiles?.length;
  }

  isAnnotActiveUser() {
    let isValid = false;
    if (this.activeAnnotation?.type === 'click') {
      if ((this.activeAnnotation?.data as any)?.comments?.[0]?.meta?.user_id === this.authUser.username) {
        isValid = true;
      }
    }
    return isValid;
  }

  editAnnotation(args: any = null) {
    if (args.instanceId != this.instanceId || args.itemId != this.item?.id) {
      return;
    }
    if (this.annotationPlugin && this.pluginReady) {
      this.annotationPlugin.fire('editingAnnotation', { annotation: args.annotation || this.activeAnnotation.data });
    }
  }

  saveAnnotation(aiParams: AiOptions) {
    if ((!this.annotationComment && !this.newFiles?.length) || this.isBusy) {
      return;
    }
    this.isBusy = true;
    if (this.newFiles.length && !this.annotationComment) {
      this.annotationComment = ' ';
    }
    if (!this.editingMode) {
      this.addNewAnnotation(aiParams);
    } else if (!this.annotatingMode && this.editingMode) {
      this.saveEditedAnnotation(aiParams);
    }
  }

  saveEditedAnnotation(aiParams: AiOptions) {
    if (this.annotationPlugin && this.editingMode && this.pluginReady) {
      this.imageHandler.saveEditedAnnotation(this.annotationComment, aiParams);
      // this.annotationPlugin.fire('saveEditedAnnotation', { annotationId: this.editingAnnotation.id });
    }
  }

  addNewAnnotation(aiParams: AiOptions) {
    this.annotationService.updateCommentListVisibility(this.privateCondition);
    if (this.annotationPlugin && this.annotatingMode && this.pluginReady) {
      this.imageHandler.saveAnnotation(this.annotationComment, aiParams);
      // this.annotationPlugin.fire('saveAnnotation');
    } else if (this.annotationPlugin && this.pluginReady && !this.annotatingMode && !this.editingMode) {
      // this.annotationPlugin.fire('saveGeneralComment');
      this.imageHandler.saveAnnotation(this.annotationComment, aiParams);
      this.generalCommentingMode = false;
    }
  }

  canceladdNewAnnotation() {
    if (this.annotationPlugin && (this.annotatingMode || this.editingMode) && this.pluginReady) {
      this.annotationPlugin.fire('cancelAddingAnnotation');
    } else if (this.generalCommentingMode) this.resetUIComponent();
  }

  resetUIComponent() {
    this.annotationComment = '';
    this.isColorOpen = false;
    this.activeShape = '';
    this.isBusy = false;
    this.generalCommentingMode = false;
  }

  // TOGGLE ANNOTATION DISPLAY SECTION
  toggleAnnotationDisplay() {
    if (this.annotationPlugin && this.pluginReady) {
      // this.annotationPlugin.fire('toggleAnnotationMode');
      this.isAnnotationDisplayEnabled = !this.isAnnotationDisplayEnabled;

      this.imageHandler.toggleAnnotationsDisplay();
    }
  }

  // MARKER SECTION
  updateBorders(args: any) {
    // Disabled function, use args.nameColor when enabling
    if (!(args.username in this.userColors)) {
      this.userColors[args.username] = '#FFFFFF';
    }
  }

  downloadAsset() {
    this.downloadButton.downloadFile();
  }

  // resetAnnotation() {
  // 	this.annotationPlugin.teardown();

  // 	this.config = {
  // 		userInfo: this.authUser,
  // 		item: this.item,
  // 		annotationObjects: this.annotations,
  // 		instanceId: this.instanceId
  // 	}
  // 	this.initPlugin();
  // 	this.isAnnotationDisplayEnabled = true;
  // }

  updateMarker(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.item?.id) {
      return;
    }

    if (args.itemId == this.item.id) {
      this.annotationPlugin.annotationState.annotations = this.annotations;
    }
    const pluginActiveAnnotation = this.annotationPlugin?.annotationState?.activeAnnotation;
    if (pluginActiveAnnotation?.data) {
      pluginActiveAnnotation.data = args.data.find((item) => item.id === pluginActiveAnnotation?.data?.id);
      if (args.previewAnnotation && pluginActiveAnnotation.data) {
        args.previewAnnotation = pluginActiveAnnotation.data;
      }
    }
  }

  hoverInAnnotation(annotation) {
    if (!annotation?.id || !this.annotationPlugin || !this.pluginReady || this.activeAnnotation?.type === 'click') {
      return;
    }
    this.imageHandler?.drawingHandler?.highlightAnnotation(annotation.id);
  }

  hoverOutAnnotation(annotation) {
    if (!annotation?.id || !this.annotationPlugin || !this.pluginReady || this.activeAnnotation?.type === 'click') {
      return;
    }
    this.imageHandler?.drawingHandler?.unhighlightAnnotation(annotation.id);
  }

  private setHoveredAnnotationMessage(annotation: any): void {
    this.hoveredAnnotationMessage = annotation;
  }

  annotationBarClick(annotation) {
    if (this.annotationPlugin && this.pluginReady) {
      // this.hoverOutAnnotation(annotation);
      onOpenAnnotation.emit({
        annotation,
        itemId: this.item?.id,
        instanceId: this.instanceId,
      });
    }
  }

  isAnnotationActive(annotation: { id?: string }): boolean {
    return !!(this.activeAnnotation?.data?.id === annotation?.id);
  }

  isDropdownItemActive(): boolean {
    if (!this.activeAnnotation?.data) return false;
    const list = this.filteredAnnotations().filter((a) => !a.range?.isGeneralComment);
    const index = list.findIndex((a) => a.id === this.activeAnnotation.data.id);
    return index >= this.MAX_MARKERS_TO_SHOW;
  }

  /** First N annotation markers to show (non-general only) */
  visibleMarkerAnnotations(): any[] {
    return this.filteredAnnotations()
      .filter((a) => !a.range?.isGeneralComment)
      .slice(0, this.MAX_MARKERS_TO_SHOW);
  }

  /** Annotations that appear in the "+N" dropdown (non-general, index >= MAX_MARKERS_TO_SHOW) */
  annotationsForMoreDropdown(): { annotation: any; index: number }[] {
    const list = this.filteredAnnotations();
    const result: { annotation: any; index: number }[] = [];
    list.forEach((a, i) => {
      if (!a.range?.isGeneralComment && i >= this.MAX_MARKERS_TO_SHOW) {
        result.push({ annotation: a, index: i });
      }
    });
    return result;
  }

  getCommentBodyText(html: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  }

  getCommentBodyPreview(html: string, maxLength: number): string {
    const text = this.getCommentBodyText(html);
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
  }

  // IMAGE MOVE/ZOOM SECTION

  /** Current zoom as a display percentage (100% = fit-to-view baseline). */
  get imageZoom() {
    if (this.annotationPlugin && this.pluginReady) {
      const defaultZoom = this.imageHandler.defaultZoom || 1;
      const actualPercent = (this.imageHandler.canvasZoom / defaultZoom) * 100;
      return this.imageHandler.actualToDisplayPercent(actualPercent);
    }
    return 100;
  }

  /** Fixed display range (50–400%) used by slider, input, and clamping. */
  get zoomRange() {
    if (this.annotationPlugin && this.pluginReady) {
      return this.imageHandler.displayZoomRange;
    }
    return { min: 50, max: 400 };
  }

  /** Accepts a display percentage and converts it back to an actual canvas zoom level. */
  setImageZoom(zoomValue: any): void {
    if (typeof zoomValue === 'number') {
      if (this.annotationPlugin) {
        const defaultZoom = this.imageHandler.defaultZoom || 1;
        const actualPercent = this.imageHandler.displayToActualPercent(zoomValue);
        const zoomDecimal = (actualPercent / 100) * defaultZoom;
        this.imageHandler.setCanvasZoom(zoomDecimal);
        this.zoomInputValue = Math.round(this.imageZoom);
        EmitterService.get('IMAGE_ANNOTATION:ZOOMED').emit();
      }
      return;
    }

    // Handle object from other sources
    if (!zoomValue?.zoom || typeof zoomValue.zoom !== 'number') {
      console.warn('Invalid zoom value provided');
      return;
    }

    // Return early if instance/item don't match
    if (zoomValue.instanceId !== this.instanceId || zoomValue.itemId !== this.item?.id) {
      return;
    }

    this.zoomInputValue = Math.round(this.imageZoom);
  }

  /** Incremental zoom in/out using adaptive step sizes based on current display zoom. */
  setZoom(change) {
    const currentZoom = this.imageZoom;
    let newZoom;

    if (currentZoom < 100) {
      newZoom = currentZoom + change * 5;
    } else if (currentZoom <= 200) {
      newZoom = currentZoom + change * 25;
    } else {
      newZoom = currentZoom + change * 50;
    }

    newZoom = Math.max(this.zoomRange.min, Math.min(this.zoomRange.max, newZoom));

    this.setImageZoom(newZoom);
  }

  resetImageOrientation() {
    if (this.annotationPlugin && this.pluginReady) {
      this.imageHandler.centerImage(true);
      // this.annotationPlugin.fire('resetImageOrientation');
      EmitterService.get('IMAGE_ANNOTATION:RESETTED').emit();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.isFullscreen) {
      e.preventDefault();
      this.toggleFullscreenForViewer();
      return;
    }
    if (e.shiftKey && this.imageHandler) {
      if (e.code === 'Digit1') {
        e.preventDefault();
        this.resetImageOrientation();
      }
    }
    if (e.ctrlKey && this.imageHandler) {
      const target = e.target as HTMLElement;
      const isTyping =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable;
      if (!isTyping) {
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          this.setZoom(1);
        } else if (e.key === '-') {
          e.preventDefault();
          this.setZoom(-1);
        } else if (e.key === '0') {
          e.preventDefault();
          this.setImageZoom(100);
        }
      }
    }
  }

  onZoomDropdownOpen(isOpen: boolean): void {
    if (isOpen) {
      this.zoomInputValue = Math.round(this.imageZoom);
    }
  }

  applyZoomFromInput(): void {
    const parsed = Number(this.zoomInputValue);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.max(this.zoomRange.min, Math.min(this.zoomRange.max, Math.round(parsed)));
    this.setImageZoom(clamped);
    this.zoomInputValue = clamped;
  }

  toggleFullscreenForViewer(): void {
    if (this.isCompareMode) {
      this.compareViewerFullscreen = !this.compareViewerFullscreen;
      return;
    }
    this.topMediaInfoService.toggleFullscreen();
  }

  toggleImageMove() {
    if (this.annotationPlugin && this.pluginReady) {
      if (this.annotatingMode) {
        this.toolbarClicked('none');
      }
      this.imageHandler.toggleImageMove();
      // this.annotationPlugin.fire('toggleImageMove')
      EmitterService.get('IMAGE_ANNOTATION:MOVE_TOGGLED').emit();
    }
  }

  // Markers scroll
  getImageAnnotationCommentModalLeft() {
    const commentContainer = this.elementRef.nativeElement.querySelectorAll('.comment-component-container');
    const mediaContainer = this.elementRef.nativeElement.querySelectorAll('.image-annotation-component');
    if (!mediaContainer?.length || !commentContainer?.length) {
      return;
    }
    const mediaSize = mediaContainer[0].getBoundingClientRect();
    const commentSize = commentContainer[0].getBoundingClientRect();
    const index = this.filteredAnnotations().findIndex((a) => a.id == this.activeAnnotation.data.id);
    let left = 0;
    if (mediaSize.left + mediaSize.width > commentSize.left + commentSize.width) {
      left = index > -1 ? index * 30 : 0;
    } else {
      left = -1;
    }
    return left;
  }

  getImageAnnotationCommentModalWidth() {
    const mediaContainer = this.elementRef.nativeElement.querySelectorAll('.image-annotation-component');
    if (!mediaContainer?.length) {
      return;
    }
    const mediaSize = mediaContainer[0].getBoundingClientRect();
    let modalWidth: any;
    if (mediaSize.width >= 420) {
      modalWidth = 416;
    } else {
      modalWidth = mediaSize.width - 4;
    }
    return modalWidth;
  }

  startAnnotationMode() {
    const canAnnotate = !this.annotatingMode && this.annotationPlugin && this.pluginReady && !this.editingMode;

    if (canAnnotate && this.annotationToolbarActive) {
      this.annotationPlugin.fire('addingAnnotation');
    } else if (canAnnotate && !this.annotationToolbarActive) {
      this.generalCommentingMode = true;
    }
  }

  /** Gets the comment input place holder text depending on general or annot comment */
  getPlaceholderText() {
    let placeholderText;
    if (
      this.annotationPlugin &&
      this.annotationPlugin.controls &&
      (this.isAdding || this.annotationPlugin.controls?.uiState?.editing || this.annotationToolbarActive)
    ) {
      placeholderText = 'Write an annotation comment...';
    } else {
      placeholderText = 'Write a general comment...';
    }

    return placeholderText;
  }

  /** Toggle annotation toolbar display on/off using toggle */
  toggleAnnotationToolbarDisplay(annotationToolbarActive) {
    if (!this.annotationPlugin) {
      return;
    }
    if (annotationToolbarActive == true) {
      this.annotationPlugin.fire('togglePiklorColors', true); // Changes colors in palette to depict greyed out
    } else {
      this.canceladdNewAnnotation(); // Triggers canceladdnew event on the plugin
      this.annotationPlugin.fire('togglePiklorColors', false);
    }
  }

  cleanUpFiles() {
    this.newFiles = [];
    this.files = [];
    this.videoRecordFiles = [];
    this.audioRecordFiles = [];
  }

  uploadFileCallback(fileObject) {
    const { files } = fileObject;
    if (!files) {
      return;
    }
    files.map((file) => {
      if (!file.fileLocalId) {
        file.fileLocalId = uuid4();
      }
      return file;
    });
    files.forEach((file) => {
      if (file.method == 'audioRecord') {
        this.audioRecordFiles.push(file);
      } else if (file.method == 'videoRecord') {
        this.videoRecordFiles.push(file);
      } else {
        this.files.push(file);
      }
    });
    this.newFiles.push(...files);
  }

  onFileDeleted(file) {
    this.newFiles = this.newFiles.filter((f) => f.lastModified != file.lastModified);
    this.audioRecordFiles = this.newFiles.filter((f) => f.method == 'audioRecord');
    this.videoRecordFiles = this.newFiles.filter((f) => f.method == 'videoRecord');
    this.files = this.newFiles.filter((f) => !f.method);
  }

  handlePrivateConditionChange(value: AnnotationPrivateCondition) {
    this.annotationService.updatePrivateCondition(value);
  }

  isEditEnabled() {
    return (
      this.activeAnnotation?.type === 'click' &&
      (this.activeAnnotation?.data as any)?.comments?.[0]?.meta?.user_id === this.authUser.username
    );
  }
}
